import { z } from "zod";

const localDatePattern = /^\d{4}-\d{2}-\d{2}$/;

const ratingSchema = z.number().int().min(1).max(5);
const nullableRatingSchema = ratingSchema.nullable();

const nullableTrimmedText = (maxLength: number) =>
  z
    .string()
    .trim()
    .max(maxLength)
    .nullable()
    .refine((value) => value === null || value.length > 0, {
      message: "Text fields must be null or non-empty.",
    });

export const LocalDateSchema = z
  .string()
  .regex(localDatePattern, "Expected a date in YYYY-MM-DD format.");

export const UpsertDailyEntrySchema = z.object({
  wokeOnTime: z.boolean(),
  walked: z.boolean(),
  deepWorkDone: z.boolean(),
  shutdownNoteDone: z.boolean(),
  phoneFreeBedtime: z.boolean(),
  mood: nullableRatingSchema,
  energy: nullableRatingSchema,
  focus: nullableRatingSchema,
  brainFog: nullableRatingSchema,
  motivation: nullableRatingSchema,
  sleepHours: z.number().min(0).max(24).nullable(),
  sleepQuality: nullableRatingSchema,
  dietQuality: nullableRatingSchema,
  exerciseQuality: nullableRatingSchema,
  notes: nullableTrimmedText(1000),
  tomorrowFirstTask: nullableTrimmedText(240),
});

export const DailyEntryResponseSchema = UpsertDailyEntrySchema.extend({
  id: z.string(),
  userId: z.string(),
  localDate: LocalDateSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const TodayDailyEntryResponseSchema = z.object({
  localDate: LocalDateSchema,
  entry: DailyEntryResponseSchema.nullable(),
});

export const DailyEntriesRangeQuerySchema = z
  .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
  })
  .refine((value) => value.from <= value.to, {
    message: "`from` must be less than or equal to `to`.",
    path: ["to"],
  });

export const DailyEntriesListResponseSchema = z.object({
  entries: z.array(DailyEntryResponseSchema),
});

export const UpsertDailyEntryResponseSchema = z.object({
  entry: DailyEntryResponseSchema,
});

export type UpsertDailyEntryInput = z.infer<typeof UpsertDailyEntrySchema>;
export type DailyEntryResponse = z.infer<typeof DailyEntryResponseSchema>;
export type TodayDailyEntryResponse = z.infer<typeof TodayDailyEntryResponseSchema>;
export type DailyEntriesRangeQuery = z.infer<typeof DailyEntriesRangeQuerySchema>;
export type DailyEntriesListResponse = z.infer<typeof DailyEntriesListResponseSchema>;
export type UpsertDailyEntryResponse = z.infer<typeof UpsertDailyEntryResponseSchema>;
